import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Calendar, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function PublicHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', date: '', recurring: true });

  useEffect(() => { fetchHolidays(); }, []);

  const fetchHolidays = async () => {
    try { setHolidays((await api.get('/public-holidays')).data); }
    catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await api.put(`/public-holidays/${editId}`, form); toast.success('Modifie'); }
      else { await api.post('/public-holidays', form); toast.success('Cree'); }
      setOpen(false); resetForm(); fetchHolidays();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (h) => { setEditId(h.id); setForm({ name: h.name, date: h.date, recurring: h.recurring }); setOpen(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/public-holidays/${id}`); toast.success('Supprime'); fetchHolidays(); }
    catch (e) { toast.error('Erreur'); }
  };

  const resetForm = () => { setForm({ name: '', date: '', recurring: true }); setEditId(null); };

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="public-holidays-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Jours Feries</h1>
          <p className="text-slate-600 mt-1">Les heures sup. effectuees ces jours sont majorees de +100%</p>
        </div>
        <Button onClick={() => { resetForm(); setOpen(true); }} data-testid="add-holiday-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {holidays.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">Aucun jour ferie configure</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {holidays.map(h => (
            <Card key={h.id} className="border-slate-200" data-testid={`holiday-${h.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium">{h.name}</p>
                      <p className="text-sm text-slate-500">{h.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {h.recurring && <Badge variant="outline" className="text-xs">Annuel</Badge>}
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(h)}><Edit className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(h.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editId ? 'Modifier' : 'Ajouter'} un jour ferie</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Ex: Noel" data-testid="holiday-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="holiday-date-input" />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <span className="text-sm font-medium">Recurrent chaque annee</span>
              <Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} data-testid="holiday-recurring-switch" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1" data-testid="holiday-submit-btn">{editId ? 'Modifier' : 'Ajouter'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
